import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Heading } from 'components/Typography';
import { Form, FormField, SubmitButton, TextInput } from 'components/Forms';
import { ContactInfoContext, withContactInfoMachineState } from 'domains/signup/contactInfo/data/ContactInfoContext';
import { CheckboxInput } from 'components/Forms/inputs/CheckboxInput';
import { isEmailValidSchema } from 'domains/auth/util';
import { SubmitHandler } from 'react-hook-form';
import { TFunction } from 'i18next';

type EnterEmailForm = {
  email: string;
  receiveNewsletter: boolean;
};

const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterEmailForm> =>
  yup
    .object({
      email: isEmailValidSchema(t),
      receiveNewsletter: yup.boolean().default(false),
    })
    .required();

type Props = {
  defaultValues?: Partial<EnterEmailForm>;
  onSubmit: SubmitHandler<EnterEmailForm>;
};

const EnterEmail: React.FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <Form
      onSubmit={onSubmit}
      schema={getFormSchema(t)}
      defaultValues={defaultValues}
      validationMode="onSubmit"
      flashErrors={false}
    >
      <Heading level="h2" className="mb-16">
        {t(`signup.steps.enterEmail.title`)}
      </Heading>
      <Heading level="h7" className="mb-64 sm-d:mb-40">
        {t(`signup.steps.enterEmail.subtitle`)}
      </Heading>
      <FormField
        data-testid="email-input"
        component={TextInput}
        name="email"
        label={t(`signup.steps.enterEmail.email.label`)}
        placeholder="john@example.com"
        className="mb-40 sm-d:mb-40"
      />

      {/* @FIXME(juan): this throws an error in the console, maybe need to use checkboxgroup */}
      <FormField
        data-testid="receive-newsletter-input"
        component={CheckboxInput}
        name="receiveNewsletter"
        id="receiveNewsletter"
        option={{
          label: t(`signup.steps.enterEmail.newsletter.label`),
          value: 'true',
          startAdornment: true,
        }}
      />

      <SubmitButton data-testid="email-submit-button" size="lg" willRedirect={false} className="sm-d:w-full mt-64">
        {t('signup.steps.enterEmail.submit')}
      </SubmitButton>
    </Form>
  );
};

const ContactInfoWrapper = () => {
  const email = ContactInfoContext.useSelector(({ context }) => context.email);
  const receiveNewsletter = ContactInfoContext.useSelector(({ context }) => context.receiveNewsletter);
  const { send } = ContactInfoContext.useActorRef();

  const onSubmit: SubmitHandler<EnterEmailForm> = useCallback(
    ({ email, receiveNewsletter }) => {
      send({
        type: 'submitEmail',
        email,
        receiveNewsletter,
      });
    },
    [send],
  );

  return (
    <EnterEmail
      defaultValues={{
        email: email,
        receiveNewsletter: receiveNewsletter,
      }}
      onSubmit={onSubmit}
    />
  );
};

export const ContactInfoEnterEmail = withContactInfoMachineState(ContactInfoWrapper, 'enterEmail');
